import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ShieldCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function AdminLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionExpired = params.get("session_expired") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      // Guard: this entrance is owner-only. If a non-owner slips in, sign them back out.
      if (!data.is_owner) {
        await logout();
        toast.error("This entrance is for the Mission Watch owner only. Use the regular sign-in page.");
        return;
      }
      navigate("/admin/cost", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-charcoal-900 mb-10 transition-colors" data-testid="admin-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="max-w-sm w-full">
          <div className="w-10 h-10 rounded-xl bg-charcoal-900 grid place-items-center mb-6">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-charcoal-400">Owner console</p>
          <h1 className="text-3xl font-semibold tracking-tight text-charcoal-900 mt-1">Sign in to your admin dashboard</h1>
          <p className="text-charcoal-500 mt-2 text-sm">Live revenue, cost, margin, and upgrade-nudge conversion — one glance.</p>

          {sessionExpired && (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                 data-testid="admin-login-session-expired">
              Your session expired. Please sign in again to continue.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-xs font-medium text-charcoal-700">Owner email</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-300" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 pl-10 pr-3.5 rounded-xl border border-black/10 bg-white text-charcoal-900 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                  placeholder="admin@missionwatch.app" data-testid="admin-login-email" autoComplete="email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-charcoal-700">Password</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-300" />
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 pl-10 pr-3.5 rounded-xl border border-black/10 bg-white text-charcoal-900 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                  placeholder="Your admin password" data-testid="admin-login-password" autoComplete="current-password" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full h-11 rounded-xl bg-charcoal-900 text-white font-medium text-sm hover:bg-charcoal-800 transition-colors disabled:opacity-60"
              data-testid="admin-login-submit">
              {loading ? "Signing in…" : "Sign in to admin"}
            </button>
          </form>

          <div className="flex items-center justify-between mt-6">
            <Link to="/login" className="text-sm text-charcoal-500 hover:text-charcoal-900 hover:underline">
              Regular customer sign-in →
            </Link>
            <Link to="/forgot-password" className="text-sm text-charcoal-500 hover:text-charcoal-900 hover:underline">
              Forgot password?
            </Link>
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative bg-charcoal-900 tech-grid overflow-hidden">
        <div className="absolute inset-0 flex flex-col justify-center px-16">
          <p className="text-emerald-400 font-mono text-xs tracking-[0.2em] uppercase mb-4">Owner-only</p>
          <p className="text-white text-3xl font-medium leading-snug tracking-tight max-w-md">
            &ldquo;Every dollar of MRR, every cent of AI cost, every nudge that converts — in one dashboard.&rdquo;
          </p>
          <div className="mt-10 flex gap-8 opacity-70">
            <Stat label="MRR" value="Live" />
            <Stat label="Margin" value="Per client" />
            <Stat label="Funnel" value="Nudge → Upgrade" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">{label}</p>
      <p className="text-white text-sm mt-1">{value}</p>
    </div>
  );
}
